import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  Button,
  Tab,
  Tabs,
  Divider,
  Container,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  DbQuestionData,
  DetailedPlayer,
  Maintenance,
  Notification,
  WhosOnline,
} from '../room/types';
import { track } from '../../api/track';
import { PlayButton } from '../create-game/PlayButton';
import { Panels } from '../play-menu';
import { TopNavAdmin } from './TopNavAdmin';
import { Logo } from './Logo';
import { SearchBarMain } from './SearchBarMain';
import './styles.css';
import { CreateGameConfig, Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { MultipleTabsBanner } from './MultipleTabsBanner';
import { PlayButton2 } from '../create-game/PlayButton2';
import { TabLabel } from '../play-menu2/TabLabel';
import { TopNav } from './TopNav';
import { SubNav } from './SubNav';
import { NotifBadge, NotificationsPopover } from './NotificationsPopover';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { fetchGet, fetchPost } from '@par/shared/api/common';
import { Achievement, GameDb } from '@par/shared/common/types';
import { ScoreCardModal } from '../../modals/ScoreCardModal';
import { ProfileAchievementsDetailsDialog } from '../../dialog/ProfileAchievementsDetailsDialog';
import { AdminSubNav } from './AdminSubNav';
import { isMobile } from 'react-device-detect';
import MenuIcon from '@mui/icons-material/Menu';
import { MobileNavDrawer } from './MobileNavDrawer';
import { LeagueSubNav } from './LeagueSubNav';
import SearchIcon from '@mui/icons-material/Search';
import { SearchPlayersBarDialog } from '../../dialog/SearchPlayersBarDialog';
import { ProfileSubNav } from './ProfileSubNav';
import { TourneySubNav } from './TourneySubNav';

type PropTypes = {
  bannerMessage?: string;
  connected?: boolean;
  disablePlayButton: boolean;
  isLoggedIn: boolean;
  isAdmin: boolean;
  handleLogoutClick: () => void;
  lobbyGames?: Game[];
  leagueLobbyGames?: Game[];
  tournamentLobbyGames?: Game[];
  maintenance: Maintenance;
  onlinePlayer?: WhosOnline;
  onPlayClick: (onlinePlayer?: WhosOnline) => void;
  onTourneyGameCreate: (gameConfig: CreateGameConfig) => void;
  onUpdateProfile: () => void;
  openPlayMenu: boolean;
  profile?: Profile;
  showMultipleTabsBanner: boolean;
  soloMultiAsync: 'solo' | 'multiplayer' | 'teams' | 'async';
  voluntarySeedMatch: boolean;
};

export const Header = ({
  bannerMessage,
  connected,
  disablePlayButton,
  isLoggedIn,
  isAdmin,
  handleLogoutClick,
  lobbyGames,
  leagueLobbyGames,
  tournamentLobbyGames,
  maintenance,
  onlinePlayer,
  onPlayClick,
  onTourneyGameCreate,
  onUpdateProfile,
  openPlayMenu,
  profile,
  showMultipleTabsBanner,
  soloMultiAsync,
  voluntarySeedMatch,
}: PropTypes) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isLogin = location.pathname === '/login';
  const isSignUp = location.pathname === '/signup';
  const isGameView = location.pathname.includes('/game/view');
  const isUnsubscribe = location.pathname === '/unsubscribe';
  const lgBreakpoint = useMediaQuery(theme.breakpoints.down('lg'));
  const xlBreakpoint = useMediaQuery(theme.breakpoints.down('xl'));
  const [viewProfileId, setViewProfileId] = useState<string>();
  const [openGames, setOpenGames] = useState<Game[]>();
  const [openLeagueGames, setOpenLeagueGames] = useState<Game[]>();
  const [openTourneyGames, setOpenTourneyGames] = useState<Game[]>();
  const [openNotifications, setOpenNotifications] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [notifications, setNotifications] = useState<Notification[]>();
  const [newNotifications, setNewNotifications] = useState<Notification[]>([]);
  const [gameRow, setGameRow] = useState<GameDb>();
  const [questionSet, setQuestionSet] = useState<DbQuestionData[]>();
  const [openScorecardModal, setOpenScorecardModal] = useState(false);
  const [openAchievementsDialog, setOpenAchievementsDialog] = useState(false);
  const [clickedAchievement, setClickedAchievement] = useState<Achievement>();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openSearchPlayers, setOpenSearchPlayers] = useState(false);

  useEffect(() => {
    if (!profile) return;
    if (location.pathname.includes('room')) return;
    fetchNotifications();
  }, [profile]);

  useEffect(() => {
    if (location.pathname.includes('/profile')) {
      const pathSegments = getProfilePathSegments();
      setViewProfileId(pathSegments.value);
    }
  }, [location]);

  useEffect(() => {
    if (!lobbyGames) return;
    const isOpenGame = (g: Game) => {
      if (
        g.mode === 'teams' &&
        g.activePlayers.length < 8 &&
        !g.gameOver &&
        !g.gameStarted &&
        g.status !== 'Starting'
      )
        return true;
      if (
        !g.gameOver &&
        !g.gameStarted &&
        g.status !== 'Starting' &&
        g.mode !== 'solo' &&
        g.mode !== 'teams' &&
        g.activePlayers.length > 0
      )
        return true;
      return false;
    };
    const filtered = lobbyGames.filter((g) => isOpenGame(g));
    setOpenGames(filtered);
  }, [lobbyGames]);

  useEffect(() => {
    if (!leagueLobbyGames) return;
    const isOpenLeagueGame = (g: Game) => {
      if (
        g.league &&
        g.activePlayers.length < 8 &&
        !g.gameOver &&
        !g.gameStarted &&
        g.status !== 'Starting'
      )
        return true;
      return false;
    };
    const filteredLeagueGames = leagueLobbyGames.filter((g) =>
      isOpenLeagueGame(g)
    );
    setOpenLeagueGames(filteredLeagueGames);
  }, [leagueLobbyGames]);

  useEffect(() => {
    if (!tournamentLobbyGames) return;
    const isOpenTourneyGame = (g: Game) => {
      if (!g.gameOver && !g.gameStarted && g.status !== 'Starting') return true;
      return false;
    };
    const filteredTourneyGames = tournamentLobbyGames.filter((g) =>
      isOpenTourneyGame(g)
    );
    setOpenTourneyGames(filteredTourneyGames);
  }, [tournamentLobbyGames]);

  const handleLogoClick = async () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    await track(profile?.id || null, 'header.logo.link');
    navigate('/');
  };

  if (
    location.pathname === '/room' ||
    location.pathname === '/study' ||
    location.pathname.includes('/game/view')
  ) {
    return <></>;
  }

  const showReconnectingBanner = () =>
    !connected &&
    !isLogin &&
    !isSignUp &&
    !isGameView &&
    isUnsubscribe;

  const showMaintenanceBanner = () => maintenance.scheduled;

  const showMessageBanner = () => !!bannerMessage;
  const handleHelpClick = () => navigate('/help');

  const fetchNotifications = async () => {
    if (!profile?.id) return;
    const res: Notification[] = await fetchGet(
      `/notifications/profile/${profile.id}`
    );
    setNotifications(res);
    if (!profile?.lastNotificationSeen) {
      setNewNotifications(res);
      return;
    }
    const findIndex = res.findIndex(
      (notif) => notif._id === profile.lastNotificationSeen
    );
    const newNotifications = res.slice(0, findIndex);
    setNewNotifications(newNotifications);
  };

  const handleOpenNotifications = async (event: any) => {
    setAnchorEl(event.currentTarget);
    setOpenNotifications(true);
    if (!notifications || !notifications.length) return;
    await fetchPost('/profile/notification-seen', {
      profileId: profile?.id,
      notificationId: notifications[0]._id,
    });
    onUpdateProfile();
  };

  const handleCloseNotifications = () => {
    setNewNotifications([]);
    setOpenNotifications(false);
  };

  const handleViewResultsClick = async (gameId?: string) => {
    if (!gameId) return;
    const res = await fetchGet(`/games/result/${gameId}`);
    setGameRow(res.game);
    setQuestionSet(res.questionSet);
    setOpenScorecardModal(true);
  };

  const handleAchievementClick = (achievement: Achievement) => {
    setClickedAchievement(achievement);
    setOpenAchievementsDialog(true);
  };

  const getProfilePathSegments = () => {
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);
    if (segments.length >= 2 && segments[0] === 'profile') {
      return { path: segments[0], value: segments[2] };
    }
    return null;
  };

  return (
    <>
      <SearchPlayersBarDialog
        close={() => setOpenSearchPlayers(false)}
        open={openSearchPlayers}
      />
      <NotificationsPopover
        anchorEl={anchorEl}
        handleAchievementClick={handleAchievementClick}
        handleCloseNotifications={handleCloseNotifications}
        handleViewResultsClick={handleViewResultsClick}
        newNotifications={newNotifications}
        notifications={notifications}
        open={openNotifications}
      />
      {profile && gameRow && questionSet ? (
        <ScoreCardModal
          onClose={() => setOpenScorecardModal(false)}
          categorySet={gameRow.categorySet}
          gameId={gameRow.gameId}
          gameType={gameRow.gameType as 'casual' | 'pro'}
          handicap={gameRow.handicap}
          isDaily={gameRow.daily}
          league={gameRow.league}
          openScordcardModal={openScorecardModal}
          mode={gameRow.mode}
          players={gameRow.players as Record<string, DetailedPlayer>}
          profileId={profile?.id}
          questionSet={questionSet}
          seededBy={gameRow.matchMakingSeededBy}
          teams={gameRow.teams}
          ranked={gameRow.ranked}
          variant={gameRow.variant}
        />
      ) : null}
      {profile && clickedAchievement ? (
        <ProfileAchievementsDetailsDialog
          achievement={clickedAchievement}
          close={() => setOpenAchievementsDialog(false)}
          open={openAchievementsDialog}
          viewProfile={profile}
        />
      ) : null}
      <Box mb={2}>
        {/* {showMultipleTabsBanner ? <MultipleTabsBanner /> : null} */}
        <Stack textAlign={'center'} width={'100%'} bgcolor={'#ed000321'}>
          {showMessageBanner() ? (
            <Typography
              variant="body1"
              sx={{ color: theme.palette.success.main }}
            >
              {bannerMessage}
            </Typography>
          ) : null}
          {showMaintenanceBanner() ? (
            <Typography color={'error.main'} fontWeight={600} p={1}>
              {maintenance.bannerMessage}
            </Typography>
          ) : null}
          {showReconnectingBanner() ? (
            <Typography color={'warning.main'} fontWeight={600}>
              Reconnecting...
            </Typography>
          ) : null}
        </Stack>
        <header>
          <AppBar
            position="static"
            component="nav"
            sx={{
              backgroundColor: theme.palette.background.default,
              boxShadow: `0px 2px 4px -1px ${theme.palette.secondary.main}, 0px 4px 5px 0px ${theme.palette.secondary.main}, 0px 1px 10px 0px ${theme.palette.secondary.main}`,
              backgroundImage: 'none',
            }}
          >
            <Toolbar disableGutters>
              {!lgBreakpoint ? (
                <Box position="absolute" top={20} left={15}>
                  <Logo handleLogoClick={handleLogoClick} />
                </Box>
              ) : null}
              {isLoggedIn && !isMobile ? (
                <TopNav
                  handleLogoutClick={handleLogoutClick}
                  openGames={openGames}
                  openLeagueGames={openLeagueGames}
                  openTourneyGames={openTourneyGames}
                  onUpdateProfile={onUpdateProfile}
                  profile={profile}
                />
              ) : (
                <IconButton onClick={() => setOpenDrawer((prev) => !prev)}>
                  <MenuIcon />
                </IconButton>
              )}
              {isLoggedIn && isMobile ? (
                <MobileNavDrawer
                  handleDrawerClose={() => setOpenDrawer((prev) => !prev)}
                  handleLogoutClick={handleLogoutClick}
                  openGames={openGames}
                  onUpdateProfile={onUpdateProfile}
                  openDrawer={openDrawer}
                  profile={profile}
                />
              ) : null}
              {isLoggedIn ? (
                <Stack
                  direction="row"
                  spacing={2}
                  position={'absolute'}
                  right={20}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent={'right'}
                    alignItems={'center'}
                    paddingX={2}
                  >
                    {!isMobile ? (
                      <Tooltip title="search players">
                        <IconButton
                          color="primary"
                          onClick={() => setOpenSearchPlayers(true)}
                        >
                          <SearchIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <NotifBadge
                      variant={'dot'}
                      badgeContent={newNotifications?.length}
                      overlap="circular"
                    >
                      <IconButton
                        color="primary"
                        onClick={handleOpenNotifications}
                      >
                        <NotificationsIcon />
                      </IconButton>
                    </NotifBadge>
                    {!isMobile ? (
                      <Tooltip title="help and how-tos">
                        <IconButton color="primary" onClick={handleHelpClick}>
                          <HelpCenterIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {!isMobile ? (
                      <Tooltip title="logout">
                        <IconButton color="primary" onClick={handleLogoutClick}>
                          <LogoutIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Stack>
                  <PlayButton2
                    connected={connected}
                    disablePlayButton={disablePlayButton}
                    maintenanceScheduled={maintenance.scheduled}
                    onlinePlayer={onlinePlayer}
                    openPlayMenu={openPlayMenu}
                    onPlayClick={onPlayClick}
                    seedMatch={profile.seedMatch}
                    small={isMobile}
                    soloMultiAsync={soloMultiAsync}
                    voluntarySeedMatch={voluntarySeedMatch}
                  />
                </Stack>
              ) : null}
            </Toolbar>
          </AppBar>
        </header>
        {isLoggedIn && location.pathname.includes('profile') ? (
          <ProfileSubNav
            profile={profile}
            showAccountBoundMenus={viewProfileId === profile.id}
            viewProfileId={viewProfileId}
          />
        ) : null}
        {isLoggedIn && location.pathname.includes('explore') ? (
          <SubNav />
        ) : null}
        {isLoggedIn && location.pathname.includes('league') ? (
          <LeagueSubNav profile={profile} />
        ) : null}
        {isLoggedIn && location.pathname.includes('tournament') ? (
          <TourneySubNav
            onTourneyGameCreate={onTourneyGameCreate}
            profile={profile}
          />
        ) : null}
        {isLoggedIn && location.pathname.includes('admin') ? (
          <AdminSubNav profile={profile} />
        ) : null}
      </Box>
    </>
  );
};
